import styles from "./index.module.scss";
import { ModalBase } from "../ModalBase";
import { useTranslation } from "react-i18next";
import { UppyFile } from "@uppy/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    AspectRatio,
    AssetLayering,
    AssetType
} from "views/page-content/cloud/upload/types";
import {
    audioThumbnailAsync,
    imageThumbnailAsync,
    videoThumbnailAsync
} from "views/page-content/cloud/upload/upload-helpers/thumbnail-helpers";
import { AssetUploadForm } from "./AssetUploadForm";

export interface AssetUploadModalProps {
    files: UppyFile[];
    onUpload: (files: UppyFile[], fileAttributes: { [key: string]: any }) => void;
    onClose: () => void;
    isUploading: boolean;
    allProductionGroups: string[];
}

export const AssetUploadModal = ({
    onClose,
    files,
    onUpload,
    isUploading,
    allProductionGroups
}: AssetUploadModalProps) => {
    const { t } = useTranslation();

    const [fileAttributes, setFileAttributes] = useState<{
        [fileName: string]: { [key: string]: any };
    }>({});

    const [previewUrlsById, setPreviewUrlsById] = useState<Record<string, string>>(
        {}
    );

    const [isProcessingById, setIsProcessingById] = useState<Record<string, boolean>>(
        {}
    );

    // Prevent double-submit even if the underlying button doesn't honor "disabled"
    const [isUploadClickLocked, setIsUploadClickLocked] = useState(false);

    // Used to ignore outdated async work when file list changes
    const processingRunIdRef = useRef(0);

    // Run eager thumbnail generation only after initial attributes are set for this selection
    const [isInitialized, setIsInitialized] = useState(false);

    const getFingerprint = (f: UppyFile) => {
        const data = f.data;
        const lastModified = data instanceof File ? data.lastModified : "na";
        return [f.name, f.type, String(f.size), String(lastModified)].join("|");
    };

    const dedupeFiles = useMemo(() => {
        const seen = new Set<string>();
        const unique: UppyFile[] = [];

        for (const f of files) {
            const fingerprint = getFingerprint(f);
            if (seen.has(fingerprint)) continue;
            seen.add(fingerprint);
            unique.push(f);
        }

        return unique;
    }, [files]);

    const readBlobAsDataUrl = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

    const getOrientation = (attrs: any): AspectRatio =>
        attrs?.orientation === AspectRatio.vertical
            ? AspectRatio.vertical
            : AspectRatio.horizontal;

    const getAssetKind = (blob: Blob, name?: string) => {
        const mime = (blob.type || "").toLowerCase();
        if (mime.startsWith("image/")) return "image";
        if (mime.startsWith("video/")) return "video";
        if (mime.startsWith("audio/")) return "audio";

        const lowerName = (name || "").toLowerCase();
        const ext = lowerName.includes(".")
            ? lowerName.slice(lowerName.lastIndexOf(".") + 1)
            : "";

        const videoExts = new Set(["mp4", "mov", "m4v", "webm", "mkv"]);
        const imageExts = new Set(["jpg", "jpeg", "png", "gif", "webp", "bmp"]);
        const audioExts = new Set(["mp3", "wav", "m4a", "aac", "ogg", "flac"]);

        if (videoExts.has(ext)) return "video";
        if (imageExts.has(ext)) return "image";
        if (audioExts.has(ext)) return "audio";
        return "unknown";
    };

    const getPreviewUrlFromThumbnail = (thumbnail: any): string | undefined => {
        const dataUrl = thumbnail?.dataURL;
        if (typeof dataUrl === "string" && dataUrl.length > 0) return dataUrl;

        const url = thumbnail?.url;
        if (typeof url === "string" && url.length > 0) return url;

        return undefined;
    };

    useEffect(() => {
        setIsInitialized(false);

        const initialAttributes: { [fileName: string]: any } = {};
        const initialProcessing: Record<string, boolean> = {};

        dedupeFiles.forEach((file) => {
            initialAttributes[file.id] = {
                layerOption: AssetLayering.FullScreen,
                audioOnly: false,
                enableAudio: true,
                endOnBlack: false,
                orientation: AspectRatio.horizontal,
                productionGroups: [],
                thumbnailTag: ""
            };
            initialProcessing[file.id] = false;
        });

        setFileAttributes(initialAttributes);
        setIsProcessingById(initialProcessing);

        // New file set → allow upload again
        setIsUploadClickLocked(false);
        setIsInitialized(true);
    }, [dedupeFiles]);

    // When parent upload finishes, unlock for another attempt (if modal stays open)
    useEffect(() => {
        if (!isUploading) {
            setIsUploadClickLocked(false);
        }
    }, [isUploading]);

    const generateThumbnailForFile = useCallback(async (
        uppyFile: UppyFile,
        currentAttrs: { [key: string]: any }
    ) => {
        const data = uppyFile.data;
        if (!(data instanceof Blob)) return null;

        const layerOption: AssetLayering =
            currentAttrs.layerOption ?? AssetLayering.FullScreen;
        const orientation = getOrientation(currentAttrs);

        const fileNameForExt =
            typeof uppyFile.name === "string" && uppyFile.name.length > 0
                ? uppyFile.name
                : data instanceof File
                  ? data.name
                  : "";

        const kind = getAssetKind(data, fileNameForExt);


        const waitForEvent = (target: EventTarget, eventName: string): Promise<void> =>
            new Promise((resolve, reject) => {
                const onOk = () => resolve();
                const onError = () => reject(new Error("Video load failed"));
                target.addEventListener(eventName, onOk, { once: true });
                target.addEventListener("error", onError, { once: true });
            });

        const waitWithTimeout = <T,>(
            promise: Promise<T>,
            ms: number,
            label: string
        ): Promise<T> =>
            new Promise((resolve, reject) => {
                const timer = window.setTimeout(() => {
                    reject(new Error(`${label} timed out`));
                }, ms);
                promise
                    .then((v) => {
                        window.clearTimeout(timer);
                        resolve(v);
                    })
                    .catch((e) => {
                        window.clearTimeout(timer);
                        reject(e);
                    });
            });

        if (kind === "image") {
            const base64 = await readBlobAsDataUrl(data);
            const thumb = await imageThumbnailAsync(
                layerOption,
                AssetType.image,
                base64,
                960,
                orientation,
                85
            );

            return { thumbnail: thumb };
        }

        if (kind === "video" || kind === "unknown") {
            const objectUrl = URL.createObjectURL(data);
            try {
                const video = document.createElement("video");
                video.src = objectUrl;
                video.muted = true;
                video.playsInline = true;
                video.preload = "metadata";

                const waitForFirstDecodedFrame = async () => {
                    const anyVideo = video as any;

                    if (typeof anyVideo.requestVideoFrameCallback === "function") {
                        try {
                            await waitWithTimeout(
                                new Promise<void>((resolve) => {
                                    anyVideo.requestVideoFrameCallback(() => resolve());
                                }),
                                4000,
                                "requestVideoFrameCallback"
                            );
                            return;
                        } catch (e) {
                        }
                    }

                    if (video.readyState >= 2) {
                        return;
                    }

                    await waitWithTimeout(
                        Promise.race([
                            waitForEvent(video, "loadeddata"),
                            waitForEvent(video, "canplay")
                        ]),
                        12000,
                        "frame-ready"
                    );
                };

                video.load();

                try {
                    await waitWithTimeout(
                        waitForEvent(video, "loadedmetadata"),
                        6000,
                        "loadedmetadata"
                    );

                    const safeDuration =
                        Number.isFinite(video.duration) && video.duration > 0
                            ? video.duration
                            : 1;
                    const targetTime = Math.max(0, Math.min(0.1, safeDuration / 2));

                    try {
                        video.currentTime = targetTime;
                        await waitWithTimeout(waitForEvent(video, "seeked"), 4000, "seeked");
                    } catch (e) {
                    }
                } catch (e) {
                }

                await waitForFirstDecodedFrame();

                const thumb = await videoThumbnailAsync(video, 960, "", orientation);

                return { thumbnail: thumb };
            } catch (e) {
                return null;
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        }

        if (kind === "audio") {
            const tag = uppyFile.name ?? "";
            const thumb = await audioThumbnailAsync(tag, 960);
            return { thumbnail: thumb, audioOnly: true };
        }

        return null;
    }, []);

    const applyThumbnailResult = useCallback((fileId: string, patch: any) => {
        setFileAttributes((prev) => {
            const current = prev[fileId] ?? {};
            return {
                ...prev,
                [fileId]: {
                    ...current,
                    ...patch
                }
            };
        });

        const previewUrl = getPreviewUrlFromThumbnail(patch?.thumbnail);

        if (previewUrl) {
            setPreviewUrlsById((prev) => ({ ...prev, [fileId]: previewUrl }));
        }
    }, []);

    const runWithConcurrency = async <T,>(
        items: T[],
        limit: number,
        worker: (item: T) => Promise<void>
    ) => {
        const queue = [...items];
        const runners = Array.from({ length: Math.max(1, limit) }, async () => {
            while (queue.length > 0) {
                const next = queue.shift();
                if (!next) return;
                await worker(next);
            }
        });
        await Promise.all(runners);
    };

    useEffect(() => {
        if (!isInitialized) return;

        const runId = ++processingRunIdRef.current;

        const filesNeedingThumb = dedupeFiles.filter((f) => {
            const existingPreviewFromAttrs = getPreviewUrlFromThumbnail(
                fileAttributes?.[f.id]?.thumbnail
            );
            const existingPreviewFromState = previewUrlsById?.[f.id];

            return !existingPreviewFromAttrs && !existingPreviewFromState;
        });

        if (filesNeedingThumb.length === 0) return;

        filesNeedingThumb.forEach((f) => {
            setIsProcessingById((prev) => ({ ...prev, [f.id]: true }));
        });

        void runWithConcurrency(filesNeedingThumb, 2, async (uppyFile) => {
            if (processingRunIdRef.current !== runId) return;

            try {
                const currentAttrs = fileAttributes?.[uppyFile.id] ?? {};
                const patch = await generateThumbnailForFile(uppyFile, currentAttrs);
                if (!patch) return;

                if (processingRunIdRef.current !== runId) return;
                applyThumbnailResult(uppyFile.id, patch);
            } finally {
                if (processingRunIdRef.current === runId) {
                    setIsProcessingById((prev) => ({
                        ...prev,
                        [uppyFile.id]: false
                    }));
                }
            }
        });
    }, [dedupeFiles, isInitialized, applyThumbnailResult, fileAttributes, generateThumbnailForFile, previewUrlsById]);

    const buildAttributesWithThumbnails = async () => {
        // Only generate missing thumbnails; reuse ones already generated while modal is open.
        const next = { ...fileAttributes };

        await Promise.all(
            dedupeFiles.map(async (uppyFile) => {
                const current = next[uppyFile.id] ?? {};

                const hasPreviewAlready = !!getPreviewUrlFromThumbnail(
                    current?.thumbnail
                );
                if (hasPreviewAlready) return;

                const patch = await generateThumbnailForFile(uppyFile, current);
                if (!patch) return;

                next[uppyFile.id] = { ...current, ...patch };
            })
        );

        // IMPORTANT: commit into state so downstream upload helpers see attributes.thumbnail
        setFileAttributes(next);

        return next;
    };

    const anyProcessing = dedupeFiles.some((f) => isProcessingById[f.id]);
    const disableUpload = isUploading || anyProcessing || isUploadClickLocked;

    return (
        <div className={styles["asset-upload-modal"]}>
            <ModalBase
                isOpen={true}
                setIsOpen={(open) => {
                    if (!open) onClose();
                }}
                header={t("cloud-assets:upload-asset")}
                primaryButton={{
                    text: anyProcessing
                        ? t("cloud-assets:processing-assets", "Processing assets…")
                        : t("buttons:upload"),
                    onClick: async () => {
                        if (disableUpload) return;

                        // lock immediately to prevent double-clicks
                        setIsUploadClickLocked(true);

                        try {
                            const attrs = await buildAttributesWithThumbnails();

                            const filteredAttrs = dedupeFiles.reduce((memo, f) => {
                                memo[f.id] = attrs[f.id];
                                return memo;
                            }, {} as { [key: string]: any });

                            onUpload(dedupeFiles, filteredAttrs);
                        } catch (e) {
                            // If thumbnail generation fails (most common with video decode/timeout),
                            // allow the user to retry the upload.
                            setIsUploadClickLocked(false);
                            throw e;
                        }
                    },
                    disabled: disableUpload
                }}
                secondaryButton={{
                    text: t("buttons:cancel"),
                    onClick: onClose
                }}
                loading={isUploading}
            >
                {anyProcessing && (
                    <div style={{ marginBottom: 12, fontSize: 13, opacity: 0.9 }}>
                        <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                        />
                        <span style={{ marginLeft: 8 }}>
                            {t("cloud-assets:processing-assets", "Processing assets…")}
                        </span>
                    </div>
                )}

                <div className={styles["asset-upload-forms-container"]}>
                    {dedupeFiles.map((file) => {
                        const previewUrl = previewUrlsById[file.id];
                        const isProcessing = !!isProcessingById[file.id];
                        const showInlineProcessing = isProcessing && !previewUrl;

                        return (
                            <div key={file.id}>
                                {showInlineProcessing && (
                                    <div style={{ marginBottom: 8, fontSize: 12, opacity: 0.85 }}>
                                        <span
                                            className="spinner-border spinner-border-sm"
                                            role="status"
                                            aria-hidden="true"
                                        />
                                        <span style={{ marginLeft: 8 }}>
                                            {t(
                                                "cloud-assets:generating-thumbnail",
                                                "Generating preview…"
                                            )}
                                        </span>
                                    </div>
                                )}

                                <AssetUploadForm
                                    file={file}
                                    previewUrl={previewUrl}
                                    fileAttributes={fileAttributes}
                                    setFileAttributes={setFileAttributes}
                                    allProductionGroups={allProductionGroups}
                                />
                            </div>
                        );
                    })}
                </div>
            </ModalBase>
        </div>
    );
};
